import React from 'react';
import ReactDOM from 'react-dom';
import mapValues from 'lodash.mapvalues';

import { withTranslation, WithTranslation } from 'react-i18next';
import { ModuleType } from '../../../../types/modules';
import OnScrollToElement from '@wix/da-shared-react/pkg/OnScrollToElement';
import { withMobileContext } from '@wix/da-shared-react/pkg/Context/MobileContext';
import Zone, { ZoneGroup } from '../../Layout/Zone';
import { ZoneType } from '../../../../types/layout';
import decorateComponentWithProps from 'decorate-component-with-props';

import AboutMe from '../../UserWidgets/AboutMe/AboutMeBoxWide';
import Stats from '../../UserWidgets/Stats/StatsBoxWide';
import Visitors from '../../UserWidgets/RecentVisitors/RecentVisitorsBoxWide';
import {
  WatchersBoxWide as Watchers,
  WatchingBoxWide as Watching,
} from '../../UserWidgets/WatchedDeviants';
import {
  GroupsAdminBoxWide as GroupsAdmin,
  GroupsMemberBoxWide as GroupsMember,
} from '../../UserWidgets/Groups';
import MyComments from '../../UserWidgets/MyComments/MyCommentsBoxWide';
import MyBadges from '../../UserWidgets/MyBadges/MyBadgesBoxWide';
import WidgetNavMenu from '../../UserWidgets/_partials/WidgetNavMenu';
import withSectionFetchSpinner from '../../../decorators/withSectionFetchSpinner';
import SideNavBar from './SideNavBar';

import profilesCssVars from '../../../styles/variables.scss';
const scrollToChildOffsetTop = parseInt(
  profilesCssVars['navbar-sticky-height']
);
import s from './AboutSection.scss';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';

export const mapModuleTypeToWidgetComponent = {
  [ModuleType.ABOUT]: AboutMe,
  [ModuleType.STATS]: Stats,
  [ModuleType.MY_COMMENTS]: MyComments,
  [ModuleType.WATCHING]: Watching,
  [ModuleType.WATCHERS]: Watchers,
  [ModuleType.VISITORS]: Visitors,
  [ModuleType.GROUPS_ADMIN]: GroupsAdmin,
  [ModuleType.GROUPS_MEMBER]: GroupsMember,
  [ModuleType.BADGES_ACTIVITY]: MyBadges,
};

export interface Props {
  stats: object;
  routes: { key: string; hash: string }[];
  layoutVersion?: number;
  isMobile?: boolean;
}

export interface State {
  activeHash: string;
}
const defaultState = {
  activeHash: ModuleType.ABOUT,
};

class AboutSection extends React.Component<Props & WithTranslation, State> {
  state = defaultState;
  zoneProps;
  widgetRefs;

  render() {
    const { activeHash } = this.state;
    return (
      <div className={s['root']}>
        <div className={s['nav-block']}>
          <SideNavBar
            routes={this.props.routes}
            activeHash={activeHash}
            onActiveHashChange={this.updateActiveHash}
            className={s['hide-mobile']}
          />
        </div>
        <div className={s['content-block']}>
          <ZoneGroup>
            <Zone type={ZoneType.TOP_RIGHT} {...this.zoneProps} locked={true} />
            <Zone type={ZoneType.MID_RIGHT} {...this.zoneProps} />
            <Zone type={ZoneType.BOTTOM_RIGHT} {...this.zoneProps} />
          </ZoneGroup>
        </div>
        <ErrorBoundary hideOnFail>
          <OnScrollToElement
            elements={this.widgetRefs}
            offsetTop={scrollToChildOffsetTop}
            onScrollTo={this.updateActiveElement}
          />
        </ErrorBoundary>
      </div>
    );
  }

  renderWidgetNavMenu() {
    const { isMobile } = this.props;
    if (!isMobile) {
      return null;
    }
    return <WidgetNavMenu widgetMenuItemsList={this.props.routes} />;
  }

  updateActiveElement = (elem: HTMLElement) => {
    this.updateActiveHash(elem.id);
  };

  updateActiveHash = (activeHash: string) => {
    const documentLocationHash =
      document && document.location && document.location.hash;
    if (!documentLocationHash && activeHash === defaultState.activeHash) {
      return;
    }

    const hash = `#${activeHash}`;
    if (documentLocationHash !== hash) {
      this.setState({ activeHash });
      history.replaceState({}, '', hash);
    }
  };

  componentWillMount() {
    this.attachPropsToWidgets();
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextProps.layoutVersion !== this.props.layoutVersion) {
      this.attachPropsToWidgets();
      return true;
    }
    return nextState.activeHash !== this.state.activeHash;
  }

  attachPropsToWidgets() {
    // this code used to be a part of the render method and its purpose was to initialize
    // the mapModuleTypeToWidgetComponent map and inject some props to each of the widgets.
    // These props were needed for OnScrollToElement to work properly, but since this
    // was inside the render method, every time the state changed this code was executed, and
    // a new mapModuleTypeToWidgetComponent map was generated with a new set of references.
    // As a consequence, this was causing a re-render of each and every widget in the map.
    // Moving this logic into a separate method, and making it run only once per layoutVersion
    // solved the re-rendering issue.

    const { isMobile } = this.props;

    const moduleProps = {
      isHeaderStickyOnMobile: isMobile,
      headerExtraElementsOnMobile: this.renderWidgetNavMenu(),
      widgetBoxClassName: s['widget'],
    };
    const refs = [] as HTMLElement[];
    const mapModuleTypeToWrappedWidgetComponent = mapValues(
      mapModuleTypeToWidgetComponent,
      (WidgetComponent, moduleType) =>
        decorateComponentWithProps(WidgetComponent, {
          id: moduleType,
          parentRef: comp => {
            const node = ReactDOM.findDOMNode(comp);
            if (node instanceof HTMLElement) {
              refs.push(node);
            }
          },
          ...moduleProps,
        })
    );

    this.widgetRefs = refs;
    this.zoneProps = {
      mapModuleTypeToWidgetComponent: mapModuleTypeToWrappedWidgetComponent,
      className: s['zone'],
    };
  }
}

export default withMobileContext(
  withSectionFetchSpinner(withTranslation()(AboutSection))
);
